import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  computed,
  ElementRef,
  ViewChild,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription, combineLatest } from 'rxjs';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { CustomerFavoritesService, LastPriceRow } from '../../services/customer-favorites-service';

import { ProductService, Product } from '../../services/products';
import { Customer } from '../../services/customer';
import { getPrice, Pricelist, PricelistService } from '../../services/price-lists';
import { DeliveryService, ReturnRequest, CustomerReturnsResponse, CustomerReturnHeader } from '../../services/delivery.service';
import { EditReturnItemDialogComponent, EditReturnItemData } from './edit-return-item-dialog/edit-return-item-dialog-component';
import { MessageDialogService } from '../../services/message-dialog';

export interface ReturnItem {
  productId: number;
  name: string;
  code?: string;
  quantity: number;
  price: number;
  reason: string;   // per-line reason
}

@Component({
  selector: 'app-return',
  standalone: true,
  imports: [CommonModule, FormsModule, MatCardModule, MatIconModule, MatDialogModule],
  templateUrl: './return-component.html',
  styleUrls: ['./return-component.scss'],
})
export class ReturnComponent implements OnInit, OnDestroy, OnChanges {
  @Input() customer!: Customer | null;
  @Input() disabled = false;
  @Input() invoiceId?: number | null;
  @Input() loadId?: number;

  @Output() itemsChange = new EventEmitter<ReturnItem[]>();
  @Output() returnCreated = new EventEmitter();
  @Output() cleared = new EventEmitter<void>();

  products: Product[] = [];
  pricelists: Pricelist[] = [];

  searchText = '';
  isSearchOpen = false;
  maxResults = 200;

  @ViewChild('searchWrap', { static: false }) searchWrap?: ElementRef<HTMLElement>;
  @Input() returnItems: ReturnItem[] = [];
  private sub?: Subscription;

  // Previous returns data
  previousReturns: CustomerReturnHeader[] = [];
  isLoadingPreviousReturns = false;
  showPreviousReturns = false;

  // Use backend-friendly keywords so stock logic triggers properly
  reasons: string[] = ['Good Return', 'Damaged', 'Expired', 'Due'];
  selectedReason: string = 'Expired';

  total(): number {
    return (this.returnItems || []).reduce(
      (acc, it) => acc + (Number(it.quantity) || 0) * (Number(it.price) || 0),
      0
    );
  }

  constructor(
    private productService: ProductService,
    private pricelistService: PricelistService,
    private deliveryService: DeliveryService,
    private dialog: MatDialog,
    private customerFavoritesService: CustomerFavoritesService,
    private messageDialogService: MessageDialogService
  ) { }

  ngOnInit(): void {
    this.sub = combineLatest([
      this.productService.get(),
      this.pricelistService.get(),
    ]).subscribe(([prods, pls]) => {
      this.products = prods || [];
      this.pricelists = pls || [];
    });

    // Load previous returns when customer is available
    if (this.customer?.id) {
      this.loadPreviousReturns();
      this.loadCustomerLastPrices();
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reload previous returns when customer changes
    if (changes['customer'] && this.customer?.id) {
      this.loadPreviousReturns();
      this.loadCustomerLastPrices();
    }
  }

  // ---------- SEARCH ----------
  onSearchChange(val: string) {
    this.searchText = (val ?? '').toString();
    if (!this.disabled) this.isSearchOpen = true;
  }

  get filteredProducts(): Product[] {
    const q = (this.searchText || '').trim().toLowerCase();
    if (!q) return this.products.slice(0, this.maxResults);

    return this.products
      .filter((p) => {
        const name = (p.name || '').toLowerCase();
        const code = (p.default_code || '').toLowerCase();
        return name.includes(q) || code.includes(q);
      })
      .slice(0, this.maxResults);
  }

  openSearch() {
    if (this.disabled) return;
    this.isSearchOpen = true;
  }
  closeSearch(_syncHistory: boolean = true) {
    if (!this.isSearchOpen) return;
    this.isSearchOpen = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(ev: MouseEvent): void {
    const host = this.searchWrap?.nativeElement;
    if (host && !host.contains(ev.target as Node)) this.closeSearch();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeSearch();
  }

  @HostListener('window:popstate', ['$event'])
  onPopState(_event: PopStateEvent): void {
    if (this.isSearchOpen) {
      this.closeSearch(false);
    }
  }

  // ---------- PRICE ----------
  private lastPriceMap: Map<number, LastPriceRow> = new Map<number, LastPriceRow>();

  private loadCustomerLastPrices(): void {
    if (!this.customer?.id) {
      this.lastPriceMap = new Map<number, LastPriceRow>();
      return;
    }
    const payload = { customer_id: this.customer.id };
    this.customerFavoritesService.getLastPriceMap(payload).subscribe({
      next: (map) => {
        this.lastPriceMap = map || new Map<number, LastPriceRow>();
      },
      error: () => {
        this.lastPriceMap = new Map<number, LastPriceRow>();
      }
    });
  }

  getPriceForProduct(p: Product): number {
    const lastRow = this.lastPriceMap.get(p.id);
    if (lastRow && typeof lastRow.last_price === 'number' && lastRow.last_price > 0) {
      return lastRow.last_price;
    }
    const plId = this.customer?.priceListId;
    if (plId) {
      const pl = this.pricelists.find((x) => x.id === plId);
      return getPrice(pl, p);
    }
    return p.list_price || 0;
  }

  // ---------- TRACK BY ----------
  trackByProduct(_i: number, p: Product): number {
    return p.id;
  }
  trackByReturnItem(i: number, row: ReturnItem): string {
    return `${row.productId}::${(row.reason || '').toLowerCase()}::${i}`;
  }
  trackByReturnId(_i: number, returnItem: CustomerReturnHeader): number {
    return returnItem.id;
  }
  trackByReturnLineId(_i: number, lineItem: any): number {
    return lineItem.id;
  }

  // ---------- CRUD ----------
  addProduct(p: Product): void {
    if (this.disabled) return;

    const reason = this.normalizeReason(this.selectedReason || 'Good Return');
    const existing = this.returnItems.find((x) => x.productId === p.id && this.normalizeReason(x.reason) === reason);

    if (existing) {
      existing.quantity += 1;
    } else {
      this.returnItems.push({
        productId: p.id,
        name: p.name,
        code: p.default_code,
        quantity: 1,
        reason: this.selectedReason || 'Good Return',        // default line reason from dropdown
        price: this.getPriceForProduct(p),
      });
    }

    this.coalesceReturnItems();
    this.emitChange();
    this.closeSearch();
    this.searchText = '';
  }

  removeItem(row: ReturnItem): void {
    if (this.disabled) return;
    this.returnItems = this.returnItems.filter((x) => x !== row);
    this.emitChange();
  }

  openEditDialog(row: ReturnItem): void {
    if (this.disabled) return;
    
    const dialogData: EditReturnItemData = {
      productId: row.productId,
      name: row.name,
      code: row.code,
      quantity: row.quantity,
      price: row.price,
      reason: row.reason
    };

    const dialogRef = this.dialog.open(EditReturnItemDialogComponent, {
      width: '400px',
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (result.delete) {
          this.removeItem(row);
        } else {
          // Update the item with new values
          row.quantity = result.quantity;
          row.price = result.price;
          row.reason = result.reason;
          this.coalesceReturnItems(row);
          this.emitChange();
        }
      }
    });
  }

  onQtyChange(row: ReturnItem, v: any): void {
    row.quantity = Math.max(0, Number(v) || 0);
    this.coalesceReturnItems(row);
    this.emitChange();
  }

  onPriceChange(row: ReturnItem, v: any): void {
    row.price = Math.max(0, Number(v) || 0);
    this.emitChange();
  }

  onReasonChange(row: ReturnItem, v: string): void {
    row.reason = v;
    this.coalesceReturnItems(row);
    this.emitChange();
  }

  clearAll(): void {
    this.returnItems = [];
    this.emitChange();
    this.cleared.emit();
  }

  private normalizeReason(reason: string | null | undefined): string {
    return (reason || '').trim().toLowerCase();
  }

  private coalesceReturnItems(preferredRow?: ReturnItem): void {
    const grouped = new Map<string, ReturnItem>();

    for (const item of this.returnItems || []) {
      const key = `${item.productId}::${this.normalizeReason(item.reason)}`;
      const existing = grouped.get(key);

      if (!existing) {
        grouped.set(key, item);
        continue;
      }

      const primary = preferredRow && (existing === preferredRow || item === preferredRow)
        ? preferredRow
        : existing;
      const secondary = primary === existing ? item : existing;

      primary.quantity = Math.max(0, Number(primary.quantity) || 0) + Math.max(0, Number(secondary.quantity) || 0);
      if (!(Number(primary.price) > 0) && Number(secondary.price) > 0) {
        primary.price = Number(secondary.price) || 0;
      }
      grouped.set(key, primary);
    }

    this.returnItems = Array.from(grouped.values()).filter((item) => (Number(item.quantity) || 0) > 0);
  }


  /** Decide the header reason sent to backend (drives stock ops there) */
  private computeHeaderReason(): string {
    if (!this.returnItems.length) return 'Due'; // amount-only scenario
    const lower = this.returnItems.map(r => (r.reason || '').toLowerCase());
    if (lower.some(r => r.includes('expired') || r.includes('expiry'))) return 'Expired';
    if (lower.some(r => r.includes('damage') || r.includes('damaged') || r.includes('broken'))) return 'Damaged';
    return 'Good Return';
  }

  submit(): void {
    const lines = this.returnItems
      .filter((x) => x.quantity > 0)
      .map((x) => ({
        product_id: x.productId,
        quantity: x.quantity,
        price_unit: x.price,
        reason: x.reason,
      }));

    const headerReason = this.computeHeaderReason();
    const totalAmount = this.total();

    // Show confirmation dialog
    const confirmMessage = `Are you sure you want to submit this return?\n\n` +
      `Customer: ${this.customer?.name || 'Unknown'}\n` +
      `Items: ${lines.length}\n` +
      `Total Amount: ${totalAmount.toFixed(2)}\n` +
      `Reason: ${headerReason}`;

    this.messageDialogService.confirm(confirmMessage, 'Confirm Return Submission').subscribe(confirmed => {
      if (confirmed) {
        if (this.customer?.id) {
          // Case 2: Standalone return (no invoice)
          const payload = {
            load_id: this.loadId,         // <— fixed key (backend expects snake_case)
            partner_id: this.customer.id,
            reason: headerReason,         // <— use computed header reason
            lines,
            amount: totalAmount,
            invoice_id: this.invoiceId,
          };

          this.deliveryService.createStandaloneReturn(payload as any).subscribe({
            next: (res) => {
              console.log('Standalone return created:', res);
              this.returnCreated.emit();
            },
            error: (err) => {
              console.error('Failed to create standalone return', err);
            },
          });
        } else {
          console.error('Either invoiceId or customer is required to submit a return');
        }
      }
    });
  }

  emitChange(): void {
    this.itemsChange.emit(this.returnItems);
  }

  // ---------- PREVIOUS RETURNS ----------
  loadPreviousReturns(): void {
    if (!this.customer?.id) return;

    this.isLoadingPreviousReturns = true;
    const payload = {
      customerId: this.customer.id,
      loadId: this.loadId || 0
    };

    this.deliveryService.getCustomerReturns(payload).subscribe({
      next: (response: CustomerReturnsResponse) => {
        if (response.status === 'success') {
          this.previousReturns = response.returns || [];
        }
        this.isLoadingPreviousReturns = false;
      },
      error: (err) => {
        console.error('Failed to load previous returns', err);
        this.isLoadingPreviousReturns = false;
      }
    });
  }

  togglePreviousReturns(): void {
    this.showPreviousReturns = !this.showPreviousReturns;
    if (this.showPreviousReturns && this.previousReturns.length === 0 && !this.isLoadingPreviousReturns) {
      this.loadPreviousReturns();
    }
  }
}
